<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { debounce } from '@chatwoot/utils';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const props = defineProps({
  company: {
    type: Object,
    default: () => ({}),
  },
  contacts: {
    type: Array,
    default: () => [],
  },
  meta: {
    type: Object,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isBusy: {
    type: Boolean,
    default: false,
  },
  searchResults: {
    type: Array,
    default: () => [],
  },
  isSearching: {
    type: Boolean,
    default: false,
  },
  selectedContact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'cancelContactSelection',
  'confirmContactSelection',
  'removeContact',
  'search',
  'selectContact',
  'update:currentPage',
]);

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const selectedContactId = ref(null);
const searchQuery = ref('');

const hasContacts = computed(() => props.contacts.length > 0);
const currentPage = computed(() => Number(props.meta?.page || 1));
const totalContacts = computed(() => Number(props.meta?.totalCount || 0));
const linkedContactIds = computed(
  () => new Set(props.contacts.map(contact => Number(contact.id)))
);
const showPaginationFooter = computed(
  () => hasContacts.value && totalContacts.value > props.contacts.length
);

const openContact = contactId => {
  router.push({
    name: 'contacts_edit',
    params: {
      accountId: route.params.accountId,
      contactId,
    },
  });
};

const contactMeta = contact =>
  [contact.email, contact.phoneNumber].filter(Boolean).join(' • ');

const contactName = contact =>
  contact.name || t('COMPANIES.DETAIL.CONTACTS.UNNAMED_CONTACT');

const contactOptions = computed(() =>
  props.searchResults
    .filter(
      contact =>
        !contact.linkedToCurrentCompany &&
        !linkedContactIds.value.has(Number(contact.id))
    )
    .map(contact => ({
      value: contact.id,
      label: [contactName(contact), contact.email, contact.phoneNumber]
        .filter(Boolean)
        .join(' · '),
    }))
);

const emptyState = computed(() => {
  if (props.isSearching) {
    return t('COMPANIES.DETAIL.CONTACTS.LOADING');
  }

  return searchQuery.value.trim()
    ? t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.EMPTY')
    : t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.INITIAL');
});

const selectedContactName = computed(() =>
  props.selectedContact ? contactName(props.selectedContact) : ''
);

const selectedContactMeta = computed(() =>
  props.selectedContact ? contactMeta(props.selectedContact) : ''
);

const selectedContactCompanyName = computed(
  () => props.selectedContact?.company?.name || ''
);

const summaryRows = computed(() => [
  {
    key: 'company',
    label: t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.COMPANY_LABEL'),
    avatarName: props.company.name || t('COMPANIES.UNNAMED'),
    avatarSrc: props.company.avatarUrl,
    primary: props.company.name || t('COMPANIES.UNNAMED'),
    secondary: props.company.domain,
  },
  {
    key: 'contact',
    label: t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.CONTACT_LABEL'),
    badge: selectedContactCompanyName.value
      ? t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.CURRENT_COMPANY', {
          companyName: selectedContactCompanyName.value,
        })
      : '',
    avatarName: selectedContactName.value,
    avatarSrc: props.selectedContact?.thumbnail,
    primary: selectedContactName.value,
    secondary: selectedContactMeta.value,
  },
]);

const debouncedSearch = debounce(query => {
  emit('search', query);
}, 300);

const handleSearch = query => {
  searchQuery.value = query;
  debouncedSearch(query.trim());
};

const handleContactSelect = contactId => {
  const selectedContact = props.searchResults.find(
    contact => contact.id === Number(contactId)
  );

  selectedContactId.value = null;
  if (selectedContact) {
    emit('selectContact', selectedContact);
  }
};
</script>

<template>
  <div class="flex flex-col gap-6 px-5 pb-8 sm:px-6">
    <div
      v-if="!selectedContact"
      class="flex flex-col gap-4 border-b border-auth-border pb-6 dark:border-auth-border-dark"
    >
      <div class="flex flex-col gap-2">
        <label
          class="text-base font-semibold text-auth-text dark:text-auth-text-dark"
        >
          {{ t('COMPANIES.DETAIL.CONTACTS.ACTIONS.ADD') }}
        </label>
        <span class="text-sm text-auth-muted dark:text-auth-muted-dark">
          {{ t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.DESCRIPTION') }}
        </span>
      </div>
      <ComboBox
        use-api-results
        :model-value="selectedContactId"
        :options="contactOptions"
        :disabled="isBusy"
        :empty-state="emptyState"
        :search-placeholder="
          t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.SEARCH_PLACEHOLDER')
        "
        :placeholder="t('COMPANIES.DETAIL.CONTACTS.ACTIONS.ADD')"
        class="[&>div>button]:!h-10 [&>div>button]:!rounded-xl [&>div>button]:!border-auth-border [&>div>button]:!bg-auth-surface [&>div>button]:!text-auth-text dark:[&>div>button]:!border-auth-border-dark dark:[&>div>button]:!bg-auth-surface-dark dark:[&>div>button]:!text-auth-text-dark"
        @search="handleSearch"
        @update:model-value="handleContactSelect"
      />
    </div>

    <div
      v-else
      class="flex flex-col gap-4 border-b border-auth-border pb-6 dark:border-auth-border-dark"
    >
      <div class="flex flex-col gap-2">
        <label
          class="text-base font-semibold text-auth-text dark:text-auth-text-dark"
        >
          {{ t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.CONFIRM_TITLE') }}
        </label>
        <span class="text-sm text-auth-muted dark:text-auth-muted-dark">
          {{ t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.CONFIRM_DESCRIPTION') }}
        </span>
      </div>

      <div class="flex flex-col gap-4">
        <div
          v-for="row in summaryRows"
          :key="row.key"
          class="flex flex-col gap-2"
        >
          <div class="flex items-center justify-between h-5 gap-2">
            <label
              class="text-sm font-medium text-auth-text dark:text-auth-text-dark"
            >
              {{ row.label }}
            </label>
            <span
              v-if="row.badge"
              class="rounded-md bg-n-amber-3 px-2 py-0.5 text-xs text-n-amber-11 dark:bg-n-amber-4/30 dark:text-n-amber-9"
            >
              {{ row.badge }}
            </span>
          </div>

          <div
            class="flex h-[60px] items-center gap-2 rounded-xl border border-auth-border bg-auth-surface p-3 dark:border-auth-border-dark dark:bg-auth-surface-dark"
          >
            <Avatar
              :name="row.avatarName"
              :src="row.avatarSrc"
              :size="32"
              rounded-full
              hide-offline-status
            />
            <div class="flex flex-col w-full min-w-0 gap-1">
              <span
                class="truncate text-sm font-medium leading-4 text-auth-text dark:text-auth-text-dark"
              >
                {{ row.primary }}
              </span>
              <span
                v-if="row.secondary"
                class="truncate text-sm leading-4 text-auth-muted dark:text-auth-muted-dark"
              >
                {{ row.secondary }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between gap-3 mt-2">
        <Button
          variant="faded"
          color="slate"
          :label="t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.CANCEL')"
          class="w-full !rounded-xl !border-auth-border !bg-auth-surface !text-auth-text hover:!bg-auth-border dark:!border-auth-border-dark dark:!bg-auth-surface-dark dark:!text-auth-text-dark dark:hover:!bg-auth-border-dark"
          :disabled="isBusy"
          @click="emit('cancelContactSelection')"
        />
        <Button
          :label="t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.ADD')"
          :is-loading="isBusy"
          :disabled="isBusy"
          class="w-full !rounded-xl !bg-auth-action !text-white hover:!bg-auth-action-hover dark:!bg-auth-accent dark:!text-auth-brand dark:hover:!bg-auth-accent-hover"
          @click="emit('confirmContactSelection')"
        />
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between gap-3">
        <h4
          class="text-sm font-semibold text-auth-text dark:text-auth-text-dark"
        >
          {{ t('COMPANIES.DETAIL.SIDEBAR.TABS.CONTACTS') }}
        </h4>
        <span
          v-if="hasContacts"
          class="text-xs tabular-nums text-auth-muted dark:text-auth-muted-dark"
        >
          {{ t('COMPANIES.CONTACTS_COUNT', { n: totalContacts }) }}
        </span>
      </div>

      <div
        v-if="isLoading && !hasContacts"
        class="rounded-xl border border-dashed border-auth-border py-8 text-center text-sm text-auth-muted dark:border-auth-border-dark dark:text-auth-muted-dark"
      >
        {{ t('COMPANIES.DETAIL.CONTACTS.LOADING') }}
      </div>

      <div
        v-else-if="!hasContacts"
        class="rounded-xl border border-dashed border-auth-border px-4 py-8 text-center text-sm text-auth-muted dark:border-auth-border-dark dark:text-auth-muted-dark"
      >
        {{ t('COMPANIES.DETAIL.CONTACTS.EMPTY') }}
      </div>

      <div
        v-else
        class="flex flex-col divide-y divide-auth-border dark:divide-auth-border-dark"
      >
        <div
          v-for="contact in contacts"
          :key="contact.id"
          class="group/contact flex items-center gap-2 py-3"
        >
          <button
            type="button"
            class="flex min-w-0 flex-1 items-center gap-3 rounded-lg !p-0 text-start text-auth-text transition-colors hover:text-auth-action focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-auth-action focus-visible:ring-offset-2 focus-visible:ring-offset-auth-soft dark:text-auth-text-dark dark:hover:text-auth-accent dark:focus-visible:ring-auth-accent dark:focus-visible:ring-offset-auth-soft-dark"
            @click="openContact(contact.id)"
          >
            <Avatar
              :name="contactName(contact)"
              :src="contact.thumbnail"
              :size="32"
              rounded-full
              hide-offline-status
            />
            <div class="min-w-0 space-y-0.5">
              <span
                class="truncate text-sm font-medium leading-5 text-auth-text dark:text-auth-text-dark"
              >
                {{ contactName(contact) }}
              </span>
              <p
                v-if="contactMeta(contact)"
                class="truncate text-sm leading-5 text-auth-muted dark:text-auth-muted-dark"
              >
                {{ contactMeta(contact) }}
              </p>
            </div>
          </button>

          <Button
            icon="i-lucide-unlink"
            color="slate"
            variant="ghost"
            size="xs"
            class="shrink-0 opacity-70 transition-opacity sm:opacity-0 sm:group-hover/contact:opacity-100 sm:focus-visible:opacity-100"
            :disabled="isBusy"
            :title="t('COMPANIES.DETAIL.CONTACTS.ACTIONS.REMOVE')"
            :aria-label="t('COMPANIES.DETAIL.CONTACTS.ACTIONS.REMOVE')"
            @click.stop="emit('removeContact', contact.id)"
          />
        </div>
      </div>

      <PaginationFooter
        v-if="showPaginationFooter"
        current-page-info="CONTACTS_LAYOUT.PAGINATION_FOOTER.SHOWING"
        :current-page="currentPage"
        :total-items="totalContacts"
        :items-per-page="15"
        class="!bg-transparent !px-0 before:hidden"
        @update:current-page="emit('update:currentPage', $event)"
      />
    </div>
  </div>
</template>
